import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Plus, Edit, Trash2, Check, ShoppingCart, Package, Send, CheckCircle2, Clock, AlertTriangle, Upload, PackagePlus, Link, Unlink, Building2, FileText, Download, ChevronDown, ChevronRight, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VoorraadTab = ({ locatieId, locatieData, onUpdate, onVoorraadItemGewijzigd }) => {
  const [activeView, setActiveView] = useState('voorraad');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showTellingModal, setShowTellingModal] = useState(false);
  const [lokaleVoorraad, setLokaleVoorraad] = useState({});
  const voorraadTimers = useRef({});
  const [showBestellingModal, setShowBestellingModal] = useState(false);
  const [showQuickOrderModal, setShowQuickOrderModal] = useState(false);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [showLeverancierModal, setShowLeverancierModal] = useState(false);
  const [showOntvangenModal, setShowOntvangenModal] = useState(false);
  const [selectedVoorraadItem, setSelectedVoorraadItem] = useState(null);
  const [selectedBestelling, setSelectedBestelling] = useState(null);
  const [producten, setProducten] = useState([]);
  const [tellingData, setTellingData] = useState({});
  const [bestellingen, setBestellingen] = useState([]);

  const leveranciers = locatieData?.leveranciers || [];

  const fetchBestellingen = React.useCallback(async () => {
    if (!locatieId) return;
    try {
      const response = await axios.get(`${API}/locaties/${locatieId}/bestellingen`);
      setBestellingen(response.data);
    } catch (error) {
      console.error('Error fetching bestellingen:', error);
    }
  }, [locatieId]);

  const fetchProducten = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API}/voorraad-producten`);
      setProducten(response.data);
    } catch (error) {
      console.error('Error fetching producten:', error);
    }
  }, []);

  useEffect(() => {
    fetchProducten();
    fetchBestellingen();
  }, [locatieId, fetchBestellingen, fetchProducten]);

  const getStatusColor = (huidig, standaard) => {
    if (huidig >= standaard) return { bg: 'bg-emerald-500', text: 'text-emerald-600', label: 'OK' };
    if (huidig > 0 && huidig < standaard) return { bg: 'bg-amber-500', text: 'text-amber-600', label: 'Bijna op' };
    return { bg: 'bg-red-500', text: 'text-red-600', label: 'Onder minimum' };
  };

  const handleAddProduct = async (productData) => {
    try {
      await axios.post(`${API}/locaties/${locatieId}/voorraad`, productData);
      toast.success('Product toegevoegd');
      onUpdate();
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error('Fout bij toevoegen');
    }
  };

  const handleBulkImport = async (productsToAdd) => {
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const productData of productsToAdd) {
        try {
          await axios.post(`${API}/locaties/${locatieId}/voorraad`, productData);
          successCount++;
        } catch (error) {
          console.error('Error adding product:', error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} product(en) toegevoegd`);
        onUpdate();
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} product(en) konden niet worden toegevoegd`);
      }
      
      setShowBulkImportModal(false);
    } catch (error) {
      console.error('Error bulk importing:', error);
      toast.error('Fout bij importeren');
    }
  };

  // 4.4f: op telefoons PUT'te elke toetsaanslag + remount van de tab (focus weg).
  // Nu: lokale waarde, opslaan met 600 ms vertraging of bij blur, zonder loader.
  const bewaarVoorraadVeld = useCallback(async (voorraadId, field, value) => {
    const getal = value === '' || value === null || value === undefined
      ? 0 : parseInt(value, 10);
    if (Number.isNaN(getal)) return;
    try {
      await axios.put(`${API}/locaties/${locatieId}/voorraad/${voorraadId}`, { [field]: getal });
      // 6.6: de ouderstate meteen bijwerken (geen refetch, geen remount)
      onVoorraadItemGewijzigd?.(voorraadId, field, getal);
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Opslaan van de voorraad mislukt');
    }
  }, [locatieId, onVoorraadItemGewijzigd]);

  const handleUpdateQuantity = (voorraadId, field, value) => {
    const sleutel = `${voorraadId}:${field}`;
    setLokaleVoorraad(prev => ({ ...prev, [sleutel]: value }));
    clearTimeout(voorraadTimers.current[sleutel]);
    voorraadTimers.current[sleutel] = setTimeout(
      () => bewaarVoorraadVeld(voorraadId, field, value), 600);
  };

  const bewaarNu = (voorraadId, field) => {
    const sleutel = `${voorraadId}:${field}`;
    if (!(sleutel in lokaleVoorraad)) return;
    clearTimeout(voorraadTimers.current[sleutel]);
    bewaarVoorraadVeld(voorraadId, field, lokaleVoorraad[sleutel]);
  };

  const voorraadWaarde = (item, field) => {
    const sleutel = `${item._id}:${field}`;
    return sleutel in lokaleVoorraad ? lokaleVoorraad[sleutel] : (item[field] ?? 0);
  };

  const handleDelete = async (voorraadId) => {
    if (!window.confirm('Weet je zeker dat je dit product wilt verwijderen?')) return;

    try {
      await axios.delete(`${API}/locaties/${locatieId}/voorraad/${voorraadId}`);
      toast.success('Product verwijderd');
      onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  const handleUpdateLeverancier = async (voorraadId, leverancierId, leverancierNaam) => {
    try {
      await axios.put(`${API}/locaties/${locatieId}/voorraad/${voorraadId}`, {
        leverancierId: leverancierId || null,
        leverancierNaam: leverancierNaam || null
      });
      toast.success('Leverancier gekoppeld');
      onUpdate();
      setShowLeverancierModal(false);
      setSelectedVoorraadItem(null);
    } catch (error) {
      console.error('Error updating leverancier:', error);
      toast.error('Fout bij koppelen leverancier');
    }
  };

  const openLeverancierModal = (item) => {
    setSelectedVoorraadItem(item);
    setShowLeverancierModal(true);
  };

  const openVoorraadTelling = () => {
    // 6.6: vul de telling met de SAMENGEVOEGDE waarden, anders zet de telling een
    // net ingetypte voorraad stil terug naar de oude stand
    const data = {};
    voorraad.forEach(item => {
      data[item._id] = item.huidigeVoorraad;
    });
    setTellingData(data);
    setShowTellingModal(true);
  };

  const handleSaveTelling = async () => {
    try {
      // 6.6: alleen producten die echt zijn geteld PATCHen, en nooit een leeg veld
      // (parseInt('') sloeg null op)
      const gewijzigd = Object.entries(tellingData).filter(([voorraadId, waarde]) => {
        if (waarde === '' || waarde === null || waarde === undefined) return false;
        const huidig = voorraad.find(i => i._id === voorraadId)?.huidigeVoorraad;
        return parseInt(waarde, 10) !== Number(huidig ?? 0);
      });
      if (gewijzigd.length === 0) {
        toast.success('Geen wijzigingen in de telling');
        setShowTellingModal(false);
        return;
      }
      const updates = gewijzigd.map(([voorraadId, huidigeVoorraad]) =>
        axios.patch(`${API}/locaties/${locatieId}/voorraad/${voorraadId}/telling`, {
          huidigeVoorraad: parseInt(huidigeVoorraad, 10)
        })
      );
      await Promise.all(updates);
      toast.success('Voorraad telling opgeslagen');
      onUpdate();
      setShowTellingModal(false);
    } catch (error) {
      console.error('Error saving telling:', error);
      toast.error('Fout bij opslaan');
    }
  };

  const handleCreateBestelling = async (bestellingData) => {
    try {
      await axios.post(`${API}/locaties/${locatieId}/bestellingen`, bestellingData);
      toast.success('Bestelling aangemaakt');
      fetchBestellingen();
      setShowBestellingModal(false);
    } catch (error) {
      console.error('Error creating bestelling:', error);
      toast.error(error?.response?.data?.detail || 'Fout bij aanmaken bestelling');
    }
  };

  // Create multiple orders grouped by supplier
  const handleCreateMultipleBestellingen = async (ordersGroupedBySupplier) => {
    try {
      let successCount = 0;
      const results = [];
      
      for (const orderData of ordersGroupedBySupplier) {
        try {
          await axios.post(`${API}/locaties/${locatieId}/bestellingen`, orderData);
          successCount++;
          results.push({ 
            leverancier: orderData.leverancierNaam, 
            count: orderData.items.length 
          });
        } catch (error) {
          console.error('Error creating bestelling:', error);
        }
      }
      
      if (successCount > 0) {
        const summary = results.map(r => `${r.leverancier} (${r.count} producten)`).join(', ');
        toast.success(`${successCount} bestelling(en) aangemaakt: ${summary}`);
        fetchBestellingen();
      }
      
      setShowQuickOrderModal(false);
    } catch (error) {
      console.error('Error creating bestellingen:', error);
      toast.error('Fout bij aanmaken bestellingen');
    }
  };

  const handleUpdateBestellingStatus = async (bestellingId, newStatus) => {
    try {
      await axios.patch(`${API}/locaties/${locatieId}/bestellingen/${bestellingId}/status`, {
        status: newStatus
      });
      toast.success(`Bestelling gemarkeerd als ${newStatus}`);
      fetchBestellingen();
    } catch (error) {
      console.error('Error updating bestelling:', error);
      toast.error('Fout bij bijwerken status');
    }
  };

  const handleDeleteBestelling = async (bestellingId) => {
    if (!window.confirm('Weet je zeker dat je deze bestelling wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/locaties/${locatieId}/bestellingen/${bestellingId}`);
      toast.success('Bestelling verwijderd');
      fetchBestellingen();
    } catch (error) {
      console.error('Error deleting bestelling:', error);
      toast.error(error?.response?.data?.detail
        || 'Verwijderen mag alleen door een beheerder');
    }
  };

  const handleVerzenden = async (bestellingId) => {
    if (!window.confirm('Weet je zeker dat je deze bestelling wilt verzenden? Er wordt automatisch een email naar de leverancier gestuurd.')) return;
    try {
      const response = await axios.patch(`${API}/locaties/${locatieId}/bestellingen/${bestellingId}/verzend`);
      const msg = response.data?.message || 'Bestelling verzonden';
      if (response.data?.email_verstuurd) toast.success(msg);
      else toast.warning(msg);
      fetchBestellingen();
    } catch (error) {
      console.error('Error verzenden bestelling:', error);
      toast.error(error?.response?.data?.detail
        || 'Verzenden mislukt — bestelling staat nog op concept');
    }
  };

  const handleResendEmail = async (bestellingId) => {
    try {
      const response = await axios.post(`${API}/locaties/${locatieId}/bestellingen/${bestellingId}/verzend-email`);
      const msg = response.data?.message || 'Email opnieuw verstuurd';
      toast.success(msg);
      fetchBestellingen();
    } catch (error) {
      console.error('Error resend email:', error);
      toast.error('Email opnieuw versturen mislukt');
    }
  };

  const handleOpenOntvangenModal = (bestelling) => {
    setSelectedBestelling(bestelling);
    setShowOntvangenModal(true);
  };

  const handleOntvangen = async (bestellingId, ontvangenItems) => {
    try {
      await axios.patch(`${API}/locaties/${locatieId}/bestellingen/${bestellingId}/status`, {
        status: 'ontvangen',
        // 4.4e: de backend telt `items[].ontvangen` op bij de zichtbare voorraad
        items: (ontvangenItems || []).map(it => ({
          product_id: it.product_id || it.productId || null,
          naam: it.naam,
          ontvangen: it.ontvangenAantal ?? it.ontvangen ?? it.aantal,
        })),
        ontvangenItems,
      });
      toast.success('Bestelling ontvangen. Voorraad is bijgewerkt.');
      setShowOntvangenModal(false);
      setSelectedBestelling(null);
      fetchBestellingen();
      onUpdate(); // Refresh locatie data to show updated stock
    } catch (error) {
      console.error('Error updating bestelling:', error);
      toast.error('Fout bij markeren als ontvangen');
    }
  };

  const handleDownloadPDF = async (bestellingId) => {
    try {
      const response = await axios.get(
        `${API}/locaties/${locatieId}/bestellingen/${bestellingId}/pdf`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bestelling_${bestellingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF gedownload');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Fout bij downloaden PDF');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const days = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
    const months = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}, ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'concept':
        return <Badge variant="secondary" className="bg-slate-100 text-slate-600"><Clock className="w-3 h-3 mr-1" />Concept</Badge>;
      case 'verzonden':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-700"><Send className="w-3 h-3 mr-1" />Verzonden</Badge>;
      case 'ontvangen':
        return <Badge variant="secondary" className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Ontvangen</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // 5.1 (R4-a): de lokaal opgeslagen waarden meteen meenemen, zodat de
  // statusbadge en de banner "N producten hebben lage voorraad" direct kloppen
  // en niet pas na het opnieuw openen van de tab.
  const voorraad = (locatieData?.voorraad || []).map(item => ({
    ...item,
    huidigeVoorraad: Number(voorraadWaarde(item, 'huidigeVoorraad')) || 0,
    standaardVoorraad: Number(voorraadWaarde(item, 'standaardVoorraad')) || 0,
  }));

  // Get low stock items
  const lowStockItems = voorraad.filter(item => item.huidigeVoorraad < item.standaardVoorraad);

  const groupedByCategory = voorraad.reduce((acc, item) => {
    if (!acc[item.categorie]) acc[item.categorie] = [];
    acc[item.categorie].push(item);
    return acc;
  }, {});

  // Count bestellingen by status
  const conceptCount = bestellingen.filter(b => b.status === 'concept').length;
  const verzondenCount = bestellingen.filter(b => b.status === 'verzonden').length;

  return (
    <div className="space-y-6">
      {/* Toggle between Voorraad and Bestellingen */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <Button
            variant={activeView === 'voorraad' ? 'default' : 'outline'}
            onClick={() => setActiveView('voorraad')}
            className={activeView === 'voorraad' ? 'bg-tw-blue' : ''}
          >
            <Package className="w-4 h-4 mr-2" />
            Voorraad
            <Badge variant="secondary" className="ml-2">{voorraad.length}</Badge>
          </Button>
          <Button
            variant={activeView === 'bestellingen' ? 'default' : 'outline'}
            onClick={() => setActiveView('bestellingen')}
            className={activeView === 'bestellingen' ? 'bg-tw-blue' : ''}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Bestellingen
            {(conceptCount + verzondenCount) > 0 && (
              <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-700">
                {conceptCount + verzondenCount}
              </Badge>
            )}
          </Button>
        </div>
        
        {activeView === 'voorraad' && (
          <div className="flex gap-2 flex-wrap">
            {lowStockItems.length > 0 && (
              <Button 
                variant="default" 
                className="bg-amber-500 hover:bg-amber-600"
                onClick={() => setShowBestellingModal(true)}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Bestelling maken ({lowStockItems.length})
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowQuickOrderModal(true)}>
              <PackagePlus className="w-4 h-4 mr-2" />
              Bestellen
            </Button>
            <Button variant="default" className="bg-[#1f2548] hover:bg-[#2a3060]" onClick={() => setShowCatalogModal(true)}>
              <ShoppingCart className="w-4 h-4 mr-2" />
              Bestellen uit catalogus
            </Button>
            <Button variant="outline" onClick={openVoorraadTelling}>
              <Check className="w-4 h-4 mr-2" />
              Voorraad telling
            </Button>
            <Button variant="outline" onClick={() => setShowBulkImportModal(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Producten importeren
            </Button>
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Product toevoegen
            </Button>
          </div>
        )}
      </div>

      {/* VOORRAAD VIEW */}
      {activeView === 'voorraad' && (
        <>
          {/* Low Stock Alert */}
          {lowStockItems.length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">{lowStockItems.length} producten hebben lage voorraad</span>
                </div>
              </CardContent>
            </Card>
          )}

          {Object.keys(groupedByCategory).length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <p className="text-slate-500">Geen voorraad toegevoegd</p>
              <Button className="mt-4" onClick={() => setShowAddModal(true)}>
                Eerste product toevoegen
              </Button>
            </div>
          ) : (
            Object.entries(groupedByCategory).map(([categorie, items]) => (
              <div key={categorie} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-700">
                    {categorie === 'Borstels' && '🧹 '}
                    {categorie === 'Chemie' && '🧼 '}
                    {categorie === 'Techniek' && '🔧 '}
                    {categorie === 'Shop producten' && '🛒 '}
                    {categorie}
                  </h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Leverancier</TableHead>
                      <TableHead>Eenheid</TableHead>
                      <TableHead className="text-center">Standaard</TableHead>
                      <TableHead className="text-center">Voorraad</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Acties</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => {
                      const status = getStatusColor(item.huidigeVoorraad, item.standaardVoorraad);
                      return (
                        <TableRow key={item._id}>
                          <TableCell className="font-medium">{item.naam}</TableCell>
                          <TableCell>
                            <button
                              onClick={() => openLeverancierModal(item)}
                              className={`flex items-center gap-1 text-sm hover:underline ${
                                item.leverancierNaam 
                                  ? 'text-blue-600' 
                                  : 'text-slate-400 italic'
                              }`}
                            >
                              {item.leverancierNaam ? (
                                <>
                                  <Building2 className="w-3 h-3" />
                                  {item.leverancierNaam}
                                </>
                              ) : (
                                <>
                                  <Link className="w-3 h-3" />
                                  Koppelen
                                </>
                              )}
                            </button>
                          </TableCell>
                          <TableCell className="text-slate-500">{item.eenheid}</TableCell>
                          <TableCell className="text-center">
                            <Input
                              type="number"
                              value={voorraadWaarde(item, 'standaardVoorraad')}
                              onChange={(e) => handleUpdateQuantity(item._id, 'standaardVoorraad', e.target.value)}
                              onBlur={() => bewaarNu(item._id, 'standaardVoorraad')}
                              className="w-20 mx-auto text-center"
                              data-testid={`standaard-voorraad-${item._id}`}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Input
                              type="number"
                              value={voorraadWaarde(item, 'huidigeVoorraad') ?? ''}
                              onChange={(e) => handleUpdateQuantity(item._id, 'huidigeVoorraad', e.target.value)}
                              onBlur={() => bewaarNu(item._id, 'huidigeVoorraad')}
                              className="w-20 mx-auto text-center"
                              data-testid={`huidige-voorraad-${item._id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 text-sm ${status.text}`}>
                              <span className={`w-2 h-2 rounded-full ${status.bg}`}></span>
                              {status.label}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(item._id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ))
          )}
        </>
      )}

      {/* BESTELLINGEN VIEW */}
      {activeView === 'bestellingen' && (
        <BestellingenView
          bestellingen={bestellingen}
          formatDate={formatDate}
          getStatusBadge={getStatusBadge}
          onVerzenden={handleVerzenden}
          onOntvangen={handleOpenOntvangenModal}
          onDelete={handleDeleteBestelling}
          onDownloadPDF={handleDownloadPDF}
          onResendEmail={handleResendEmail}
        />
      )}

      {/* Add Product Modal */}
      <AddProductModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        producten={producten}
        onAdd={handleAddProduct}
        existingProducts={voorraad}
        leveranciers={leveranciers}
      />

      {/* Bulk Import Modal */}
      <BulkImportModal
        open={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        producten={producten}
        existingProducts={voorraad}
        onImport={handleBulkImport}
        leveranciers={leveranciers}
      />

      {/* Quick Order Modal */}
      <QuickOrderModal
        open={showQuickOrderModal}
        onClose={() => setShowQuickOrderModal(false)}
        voorraadItems={voorraad}
        leveranciers={leveranciers}
        onSubmit={handleCreateMultipleBestellingen}
      />

      {/* Leverancier Koppelen Modal */}
      <LeverancierKoppelenModal
        open={showLeverancierModal}
        onClose={() => {
          setShowLeverancierModal(false);
          setSelectedVoorraadItem(null);
        }}
        item={selectedVoorraadItem}
        leveranciers={leveranciers}
        onSave={handleUpdateLeverancier}
      />

      {/* Bestelling Modal */}
      <BestellingModal
        open={showBestellingModal}
        onClose={() => setShowBestellingModal(false)}
        lowStockItems={lowStockItems}
        leveranciers={locatieData?.leveranciers || []}
        onSubmit={handleCreateBestelling}
      />

      {/* Catalog Order Modal */}
      <CatalogOrderModal
        open={showCatalogModal}
        onClose={() => setShowCatalogModal(false)}
        locatieId={locatieId}
        onSubmit={async (data) => {
          await handleCreateBestelling(data);
          setShowCatalogModal(false);
        }}
      />

      {/* Ontvangen Modal */}
      <OntvangenModal
        open={showOntvangenModal}
        onClose={() => {
          setShowOntvangenModal(false);
          setSelectedBestelling(null);
        }}
        bestelling={selectedBestelling}
        onConfirm={handleOntvangen}
      />

      {/* Voorraad Telling Modal */}
      <Dialog open={showTellingModal} onOpenChange={setShowTellingModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Voorraad Telling</DialogTitle>
            <DialogDescription>
              Loop door je voorraad en vul de huidige aantallen in
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {Object.entries(groupedByCategory).map(([categorie, items]) => (
              <div key={categorie}>
                <h4 className="font-semibold text-slate-700 mb-2">
                  {categorie === 'Borstels' && '🧹 '}
                  {categorie === 'Chemie' && '🧼 '}
                  {categorie === 'Techniek' && '🔧 '}
                  {categorie}
                </h4>
                <div className="space-y-2">
                  {items.map((item) => (
                    <div key={item._id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                      <span className="text-slate-700">{item.naam}</span>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={tellingData[item._id] ?? ''}
                          onChange={(e) => setTellingData(prev => ({
                            ...prev,
                            [item._id]: e.target.value
                          }))}
                          className="w-20 text-center"
                        />
                        <span className="text-slate-400 text-sm">/ {item.standaardVoorraad}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTellingModal(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSaveTelling}>Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AddProductModal = ({ open, onClose, producten, onAdd, existingProducts = [], leveranciers = [] }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [categorie, setCategorie] = useState('');
  const [standaardVoorraad, setStandaardVoorraad] = useState(0);
  const [huidigeVoorraad, setHuidigeVoorraad] = useState(0);
  const [selectedLeverancier, setSelectedLeverancier] = useState(null);

  // Find supplier by category (leverancierCategorie from product catalog)
  const findSupplierByCategory = (leverancierCategorie) => {
    if (!leverancierCategorie) return null;
    return leveranciers.find(l => 
      l.categorie?.toLowerCase() === leverancierCategorie?.toLowerCase()
    );
  };

  // Auto-suggest leverancier when product changes
  // Priority: leverancierCategorie from product catalog > product category
  React.useEffect(() => {
    if (selectedProduct) {
      // First try leverancierCategorie from product catalog
      let suggested = findSupplierByCategory(selectedProduct.leverancierCategorie);
      
      // Fallback to product category
      if (!suggested) {
        suggested = findSupplierByCategory(selectedProduct.categorie);
      }
      
      if (suggested) {
        setSelectedLeverancier(suggested);
      } else {
        setSelectedLeverancier(null);
      }
    }
  }, [selectedProduct]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    if (!selectedProduct) {
      toast.error('Selecteer een product');
      return;
    }

    onAdd({
      productId: selectedProduct._id,
      categorie: selectedProduct.categorie,
      naam: selectedProduct.naam,
      eenheid: selectedProduct.eenheid,
      standaardVoorraad: parseInt(standaardVoorraad, 10) || 0,
      huidigeVoorraad: parseInt(huidigeVoorraad, 10) || 0,
      isCustom: false,
      leverancierId: selectedLeverancier?._id || null,
      leverancierNaam: selectedLeverancier?.bedrijfsnaam || null
    });

    // Reset
    setSelectedProduct(null);
    setStandaardVoorraad(0);
    setHuidigeVoorraad(0);
    setSelectedLeverancier(null);
  };

  const categorien = [...new Set(producten.map(p => p.categorie))];
  
  // Filter out products that already exist in voorraad
  const existingProductNames = existingProducts.map(p => p.naam);
  const availableProducts = producten.filter(p => !existingProductNames.includes(p.naam));
  
  const filteredProducten = categorie && categorie !== 'all'
    ? availableProducts.filter(p => p.categorie === categorie)
    : availableProducts;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Product toevoegen</DialogTitle>
          <DialogDescription>
            Selecteer een product uit de catalogus en stel voorraad in
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Categorie</Label>
            <Select value={categorie} onValueChange={setCategorie}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer categorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle categorieën</SelectItem>
                {categorien.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Product</Label>
            <Select
              value={selectedProduct?._id}
              onValueChange={(id) => setSelectedProduct(producten.find(p => p._id === id))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer product" />
              </SelectTrigger>
              <SelectContent>
                {filteredProducten.map(product => (
                  <SelectItem key={product._id} value={product._id}>
                    {product.naam} ({product.eenheid})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Leverancier</Label>
            <Select
              value={selectedLeverancier?._id || 'none'}
              onValueChange={(id) => {
                if (id === 'none') {
                  setSelectedLeverancier(null);
                } else {
                  setSelectedLeverancier(leveranciers.find(l => l._id === id));
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer leverancier (optioneel)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Geen leverancier</SelectItem>
                {leveranciers.map(lev => (
                  <SelectItem key={lev._id} value={lev._id}>
                    {lev.bedrijfsnaam} {lev.categorie && `(${lev.categorie})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedProduct && selectedLeverancier && (
              <p className="text-xs text-blue-600 mt-1">
                💡 Leverancier automatisch gesuggereerd op basis van {selectedProduct.leverancierCategorie ? 'leverancier categorie' : 'product categorie'}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Standaard voorraad</Label>
              <Input
                type="number"
                value={standaardVoorraad}
                onChange={(e) => setStandaardVoorraad(e.target.value)}
                placeholder="Bijv. 5"
              />
            </div>
            <div>
              <Label>Huidige voorraad</Label>
              <Input
                type="number"
                value={huidigeVoorraad}
                onChange={(e) => setHuidigeVoorraad(e.target.value)}
                placeholder="Bijv. 3"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSubmit}>Toevoegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Bulk Import Modal Component
const BulkImportModal = ({ open, onClose, producten, existingProducts = [], onImport, leveranciers = [] }) => {
  const [selectedProducts, setSelectedProducts] = useState({});
  const [defaultStock, setDefaultStock] = useState(5);

  // Filter out products that already exist
  const existingProductNames = existingProducts.map(p => p.naam);
  const availableProducts = producten.filter(p => !existingProductNames.includes(p.naam));

  // Group by category
  const groupedProducts = availableProducts.reduce((acc, product) => {
    if (!acc[product.categorie]) acc[product.categorie] = [];
    acc[product.categorie].push(product);
    return acc;
  }, {});

  // Find a supplier based on leverancierCategorie
  const findSupplierByCategory = (leverancierCategorie) => {
    if (!leverancierCategorie) return null;
    return leveranciers.find(l => 
      l.categorie?.toLowerCase() === leverancierCategorie?.toLowerCase()
    );
  };

  // Reset when modal opens
  React.useEffect(() => {
    if (open) {
      setSelectedProducts({});
      setDefaultStock(5);
    }
  }, [open]);

  const toggleProduct = (product) => {
    setSelectedProducts(prev => {
      if (prev[product._id]) {
        const { [product._id]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [product._id]: {
          ...product,
          standaardVoorraad: defaultStock,
          huidigeVoorraad: 0
        }
      };
    });
  };

  const updateStock = (productId, value) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        standaardVoorraad: parseInt(value) || 0
      }
    }));
  };

  const selectAll = (categorie) => {
    const categoryProducts = groupedProducts[categorie] || [];
    const newSelected = { ...selectedProducts };
    
    categoryProducts.forEach(product => {
      if (!newSelected[product._id]) {
        newSelected[product._id] = {
          ...product,
          standaardVoorraad: defaultStock,
          huidigeVoorraad: 0
        };
      }
    });
    
    setSelectedProducts(newSelected);
  };

  const deselectAll = (categorie) => {
    const categoryProducts = groupedProducts[categorie] || [];
    const newSelected = { ...selectedProducts };
    
    categoryProducts.forEach(product => {
      delete newSelected[product._id];
    });
    
    setSelectedProducts(newSelected);
  };

  const handleImport = () => {
    const productsToAdd = Object.values(selectedProducts).map(product => {
      // Auto-link supplier based on leverancierCategorie
      const matchingSupplier = findSupplierByCategory(product.leverancierCategorie);
      
      return {
        productId: product._id,
        categorie: product.categorie,
        naam: product.naam,
        eenheid: product.eenheid,
        standaardVoorraad: product.standaardVoorraad,
        huidigeVoorraad: product.huidigeVoorraad,
        isCustom: false,
        leverancierId: matchingSupplier?._id || null,
        leverancierNaam: matchingSupplier?.bedrijfsnaam || null
      };
    });

    if (productsToAdd.length === 0) {
      toast.error('Selecteer minimaal 1 product');
      return;
    }

    onImport(productsToAdd);
  };

  const selectedCount = Object.keys(selectedProducts).length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Producten importeren
          </DialogTitle>
          <DialogDescription>
            Selecteer meerdere producten om in één keer toe te voegen aan de voorraad
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Default stock setting */}
          <Card className="bg-slate-50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Standaard voorraad voor nieuwe producten</Label>
                  <p className="text-xs text-slate-500">Dit wordt toegepast op alle geselecteerde producten</p>
                </div>
                <Input
                  type="number"
                  value={defaultStock}
                  onChange={(e) => setDefaultStock(parseInt(e.target.value) || 0)}
                  className="w-24 text-center"
                />
              </div>
            </CardContent>
          </Card>

          {availableProducts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Alle producten zijn al toegevoegd aan de voorraad</p>
            </div>
          ) : (
            Object.entries(groupedProducts).map(([categorie, products]) => {
              const selectedInCategory = products.filter(p => selectedProducts[p._id]).length;
              
              return (
                <div key={categorie} className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-4 py-3 flex items-center justify-between">
                    <h4 className="font-semibold text-slate-700">
                      {categorie === 'Borstels' && '🧹 '}
                      {categorie === 'Chemie' && '🧼 '}
                      {categorie === 'Techniek' && '🔧 '}
                      {categorie === 'Shop producten' && '🛒 '}
                      {categorie}
                      <Badge variant="outline" className="ml-2">
                        {selectedInCategory}/{products.length}
                      </Badge>
                    </h4>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => selectAll(categorie)}>
                        Alles
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deselectAll(categorie)}>
                        Geen
                      </Button>
                    </div>
                  </div>
                  <div className="divide-y">
                    {products.map(product => {
                      const isSelected = !!selectedProducts[product._id];
                      const matchingSupplier = findSupplierByCategory(product.leverancierCategorie);
                      
                      return (
                        <div 
                          key={product._id}
                          className={`flex items-center gap-4 px-4 py-3 ${isSelected ? 'bg-blue-50' : ''}`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleProduct(product)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{product.naam}</span>
                              <span className="text-slate-500 text-sm">({product.eenheid})</span>
                            </div>
                            {product.leverancierCategorie && (
                              <div className="text-xs mt-0.5">
                                {matchingSupplier ? (
                                  <span className="text-emerald-600">
                                    ✓ Wordt gekoppeld aan: {matchingSupplier.bedrijfsnaam}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">
                                    Leverancier categorie: {product.leverancierCategorie} (geen match)
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          {isSelected && (
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-slate-500">Standaard:</Label>
                              <Input
                                type="number"
                                value={selectedProducts[product._id]?.standaardVoorraad || 0}
                                onChange={(e) => updateStock(product._id, e.target.value)}
                                className="w-20 text-center"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <div className="flex items-center gap-4 w-full justify-between">
            <span className="text-sm text-slate-500">
              {selectedCount} product(en) geselecteerd
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Annuleren</Button>
              <Button onClick={handleImport} disabled={selectedCount === 0}>
                <Upload className="w-4 h-4 mr-2" />
                Importeren ({selectedCount})
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Quick Order Modal - Order directly from inventory, grouped by supplier
const QuickOrderModal = ({ open, onClose, voorraadItems, leveranciers, onSubmit }) => {
  const [selectedItems, setSelectedItems] = useState({});
  const [notities, setNotities] = useState('');

  // Reset when modal opens
  React.useEffect(() => {
    if (open) {
      setSelectedItems({});
      setNotities('');
    }
  }, [open]);

  // Group items by their linked supplier
  const groupedBySupplier = voorraadItems.reduce((acc, item) => {
    const supplierKey = item.leverancierId || 'no-supplier';
    const supplierName = item.leverancierNaam || 'Geen leverancier gekoppeld';
    
    if (!acc[supplierKey]) {
      acc[supplierKey] = {
        leverancierId: item.leverancierId,
        leverancierNaam: supplierName,
        items: []
      };
    }
    acc[supplierKey].items.push(item);
    return acc;
  }, {});

  // Sort: suppliers first, then "no supplier" at the end
  const sortedGroups = Object.entries(groupedBySupplier).sort(([keyA], [keyB]) => {
    if (keyA === 'no-supplier') return 1;
    if (keyB === 'no-supplier') return -1;
    return 0;
  });

  const toggleItem = (item) => {
    setSelectedItems(prev => {
      if (prev[item._id]) {
        const { [item._id]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [item._id]: {
          selected: true,
          aantal: Math.max(1, item.standaardVoorraad - item.huidigeVoorraad),
          naam: item.naam,
          eenheid: item.eenheid,
          categorie: item.categorie,
          leverancierId: item.leverancierId,
          leverancierNaam: item.leverancierNaam
        }
      };
    });
  };

  const updateAantal = (itemId, aantal) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        aantal: parseInt(aantal) || 0
      }
    }));
  };

  const handleSubmit = () => {
    const selectedList = Object.entries(selectedItems)
      .filter(([_, data]) => data.aantal > 0);

    if (selectedList.length === 0) {
      toast.error('Selecteer minimaal 1 product');
      return;
    }

    // Group selected items by supplier
    const ordersBySupplier = selectedList.reduce((acc, [itemId, data]) => {
      const supplierKey = data.leverancierId || 'no-supplier';
      const supplierName = data.leverancierNaam || 'Geen leverancier';
      
      if (!acc[supplierKey]) {
        acc[supplierKey] = {
          // 4.4b: zonder leverancierId kan de bestelling nooit verstuurd worden
          leverancierId: data.leverancierId || null,
          leverancierNaam: supplierName,
          items: [],
          notities
        };
      }
      
      acc[supplierKey].items.push({
        productId: itemId,
        naam: data.naam,
        aantal: data.aantal,
        eenheid: data.eenheid,
        categorie: data.categorie
      });
      
      return acc;
    }, {});

    // Convert to array and submit
    const ordersArray = Object.values(ordersBySupplier);
    const zonderLeverancier = ordersArray.filter(o => !o.leverancierId);
    if (zonderLeverancier.length > 0) {
      toast.error('Koppel eerst een leverancier aan deze producten — zonder leverancier '
        + 'kan de bestelling niet verstuurd worden');
      return;
    }
    onSubmit(ordersArray);
  };

  const selectedCount = Object.keys(selectedItems).length;
  const totalItems = Object.values(selectedItems).reduce((sum, i) => sum + (i.aantal || 0), 0);

  // Count orders per supplier for preview
  const ordersPreview = Object.entries(selectedItems).reduce((acc, [_, data]) => {
    const supplierName = data.leverancierNaam || 'Geen leverancier';
    acc[supplierName] = (acc[supplierName] || 0) + 1;
    return acc;
  }, {});
  const ordersCount = Object.keys(ordersPreview).length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <PackagePlus className="w-5 h-5" />
            Producten bestellen
          </DialogTitle>
          <DialogDescription>
            Producten worden automatisch gegroepeerd per gekoppelde leverancier
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {voorraadItems.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Geen producten in voorraad</p>
            </div>
          ) : (
            sortedGroups.map(([supplierKey, group]) => {
              const isNoSupplier = supplierKey === 'no-supplier';
              const selectedInGroup = group.items.filter(item => selectedItems[item._id]).length;
              
              return (
                <div 
                  key={supplierKey} 
                  className={`border rounded-lg overflow-hidden ${isNoSupplier ? 'border-slate-300' : 'border-blue-200'}`}
                >
                  <div className={`px-4 py-3 flex items-center justify-between ${isNoSupplier ? 'bg-slate-100' : 'bg-blue-50'}`}>
                    <div className="flex items-center gap-2">
                      {isNoSupplier ? (
                        <Unlink className="w-4 h-4 text-slate-400" />
                      ) : (
                        <Building2 className="w-4 h-4 text-blue-600" />
                      )}
                      <h4 className={`font-semibold ${isNoSupplier ? 'text-slate-500' : 'text-blue-700'}`}>
                        {group.leverancierNaam}
                      </h4>
                      {selectedInGroup > 0 && (
                        <Badge className={isNoSupplier ? 'bg-slate-200 text-slate-700' : 'bg-blue-100 text-blue-700'}>
                          {selectedInGroup} geselecteerd
                        </Badge>
                      )}
                    </div>
                    <span className="text-sm text-slate-500">
                      {group.items.length} product(en)
                    </span>
                  </div>
                  <div className="divide-y">
                    {group.items.map(item => {
                      const isSelected = !!selectedItems[item._id];
                      const suggestedAmount = Math.max(1, item.standaardVoorraad - item.huidigeVoorraad);
                      const isLowStock = item.huidigeVoorraad < item.standaardVoorraad;
                      
                      return (
                        <div 
                          key={item._id}
                          className={`flex items-center gap-3 px-4 py-3 ${isSelected ? 'bg-blue-50' : ''}`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleItem(item)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{item.naam}</span>
                              {isLowStock && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">Laag</Badge>
                              )}
                            </div>
                            <div className="text-xs text-slate-500">
                              {item.categorie} • Voorraad: {item.huidigeVoorraad} / {item.standaardVoorraad} {item.eenheid}
                            </div>
                          </div>
                          {isSelected && (
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Input
                                type="number"
                                min="1"
                                value={selectedItems[item._id]?.aantal || suggestedAmount}
                                onChange={(e) => updateAantal(item._id, e.target.value)}
                                className="w-20 text-center"
                              />
                              <span className="text-slate-500 text-sm">{item.eenheid}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}

          {/* Notities */}
          <div>
            <Label>Notities (optioneel)</Label>
            <Textarea
              value={notities}
              onChange={(e) => setNotities(e.target.value)}
              placeholder="Extra opmerkingen voor alle bestellingen..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <div className="flex flex-col gap-2 w-full">
            {/* Preview of orders to be created */}
            {selectedCount > 0 && (
              <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                <strong>{ordersCount} bestelling(en)</strong> worden aangemaakt:
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(ordersPreview).map(([supplier, count]) => (
                    <Badge key={supplier} variant="outline">
                      {supplier}: {count} product(en)
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">
                {selectedCount} product(en), {totalItems} items totaal
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>Annuleren</Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={selectedCount === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Bestellingen maken
                </Button>
              </div>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Leverancier Koppelen Modal
const LeverancierKoppelenModal = ({ open, onClose, item, leveranciers, onSave }) => {
  const [selectedLeverancierId, setSelectedLeverancierId] = useState('none');

  // Reset when modal opens with new item
  React.useEffect(() => {
    if (open && item) {
      setSelectedLeverancierId(item.leverancierId || 'none');
    }
  }, [open, item]);

  const handleSave = () => {
    if (selectedLeverancierId === 'none') {
      onSave(item._id, null, null);
    } else {
      const leverancier = leveranciers.find(l => l._id === selectedLeverancierId);
      onSave(item._id, leverancier?._id, leverancier?.bedrijfsnaam);
    }
  };

  // Find suggested leverancier based on product category
  const suggestedLeverancier = leveranciers.find(l => 
    l.categorie?.toLowerCase() === item?.categorie?.toLowerCase()
  );

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="w-5 h-5" />
            Leverancier koppelen
          </DialogTitle>
          <DialogDescription>
            Koppel een leverancier aan <strong>{item.naam}</strong>
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-sm">
              <span className="text-slate-500">Product:</span>{' '}
              <span className="font-medium">{item.naam}</span>
            </div>
            <div className="text-sm mt-1">
              <span className="text-slate-500">Categorie:</span>{' '}
              <span className="font-medium">{item.categorie}</span>
            </div>
            {item.leverancierNaam && (
              <div className="text-sm mt-1">
                <span className="text-slate-500">Huidige leverancier:</span>{' '}
                <span className="font-medium text-blue-600">{item.leverancierNaam}</span>
              </div>
            )}
          </div>

          <div>
            <Label>Selecteer leverancier</Label>
            <Select
              value={selectedLeverancierId}
              onValueChange={setSelectedLeverancierId}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Selecteer leverancier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-slate-400 italic">Geen leverancier</span>
                </SelectItem>
                {leveranciers.map(lev => (
                  <SelectItem key={lev._id} value={lev._id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <span>{lev.bedrijfsnaam}</span>
                      {lev.categorie && (
                        <Badge variant="outline" className="text-xs">
                          {lev.categorie}
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {suggestedLeverancier && selectedLeverancierId === 'none' && (
              <button
                onClick={() => setSelectedLeverancierId(suggestedLeverancier._id)}
                className="text-xs text-blue-600 hover:underline mt-2 flex items-center gap-1"
              >
                💡 Suggestie: {suggestedLeverancier.bedrijfsnaam} (categorie: {suggestedLeverancier.categorie})
              </button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSave}>
            {selectedLeverancierId === 'none' ? (
              <>
                <Unlink className="w-4 h-4 mr-2" />
                Ontkoppelen
              </>
            ) : (
              <>
                <Link className="w-4 h-4 mr-2" />
                Koppelen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Catalog Order Modal ───────────────────────────────────────────────────
const CatalogOrderModal = ({ open, onClose, locatieId, onSubmit }) => {
  const [leveranciers, setLeveranciers] = useState([]);
  const [selectedLev, setSelectedLev] = useState('');
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [notities, setNotities] = useState('');

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/leveranciers-centraal`).then(r => setLeveranciers(r.data)).catch(() => {});
    setSelectedLev('');
    setCatalogProducts([]);
    setCart({});
    setSearch('');
    setNotities('');
  }, [open]);

  useEffect(() => {
    if (!selectedLev) { setCatalogProducts([]); return; }
    setLoadingProducts(true);
    axios.get(`${API}/producten-catalogus?leverancier_id=${selectedLev}`)
      .then(r => setCatalogProducts(r.data))
      .catch(() => setCatalogProducts([]))
      .finally(() => setLoadingProducts(false));
  }, [selectedLev]);

  const filteredProducts = catalogProducts.filter(p =>
    !search || p.naam.toLowerCase().includes(search.toLowerCase())
  );

  const updateCart = (productId, aantal) => {
    setCart(prev => {
      const copy = { ...prev };
      if (aantal <= 0) { delete copy[productId]; return copy; }
      copy[productId] = aantal;
      return copy;
    });
  };

  const selectedLevObj = leveranciers.find(l => l._id === selectedLev);
  const cartItems = Object.entries(cart).map(([pid, aantal]) => {
    const p = catalogProducts.find(cp => cp._id === pid);
    if (!p) return null;
    const sub = (p.prijs_excl_btw || 0) * aantal;
    const btw = sub * ((p.btw_percentage || 21) / 100);
    return { ...p, aantal, subtotaal: sub, btw_bedrag: btw, totaal_incl_btw: sub + btw };
  }).filter(Boolean);

  const totals = {
    sub: cartItems.reduce((s, i) => s + i.subtotaal, 0),
    btw: cartItems.reduce((s, i) => s + i.btw_bedrag, 0),
    tot: cartItems.reduce((s, i) => s + i.totaal_incl_btw, 0),
  };

  const handleSubmit = () => {
    if (cartItems.length === 0) { toast.error('Selecteer minimaal 1 product'); return; }
    onSubmit({
      leverancierId: selectedLev,
      leverancierNaam: selectedLevObj?.bedrijfsnaam || 'Onbekend',
      notities,
      items: cartItems.map(ci => ({
        naam: ci.naam,
        aantal: ci.aantal,
        eenheid: ci.beschrijving || 'per stuk',
        prijs_excl_btw: ci.prijs_excl_btw,
        btw_percentage: ci.btw_percentage || 21,
        subtotaal: ci.subtotaal,
        btw_bedrag: ci.btw_bedrag,
        totaal_incl_btw: ci.totaal_incl_btw,
        foto_url: ci.foto_url || '',
        extern_id: ci.extern_id,
      })),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bestellen uit catalogus</DialogTitle>
          <DialogDescription>Kies een leverancier en selecteer producten.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Leverancier select */}
          <div>
            <Label>Leverancier</Label>
            <Select value={selectedLev} onValueChange={setSelectedLev}>
              <SelectTrigger data-testid="catalog-leverancier-select"><SelectValue placeholder="Kies leverancier..." /></SelectTrigger>
              <SelectContent>
                {leveranciers.map(l => <SelectItem key={l._id} value={l._id}>{l.bedrijfsnaam}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {selectedLev && (
            <>
              {/* Search */}
              <Input
                placeholder="Zoek product..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="catalog-search"
              />

              {/* Product grid */}
              {loadingProducts ? (
                <div className="text-center py-8 text-slate-400">Laden...</div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  {catalogProducts.length === 0 ? 'Geen catalogus voor deze leverancier' : 'Geen producten gevonden'}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[340px] overflow-y-auto" data-testid="catalog-grid">
                  {filteredProducts.map(p => {
                    const inCart = cart[p._id] || 0;
                    return (
                      <div key={p._id} className={`rounded-lg border p-2 transition-all ${inCart > 0 ? 'border-[#1f2548] bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'}`}>
                        {p.foto_url && (
                          <img src={p.foto_url} alt="" className="w-full h-20 object-contain rounded mb-1 bg-white" loading="lazy" />
                        )}
                        <p className="text-xs font-medium text-slate-800 truncate" title={p.naam}>{p.naam}</p>
                        <p className="text-[10px] text-slate-400">{p.beschrijving}</p>
                        <p className="text-xs font-bold text-[#1f2548] mt-0.5">&euro;{(p.prijs_excl_btw || 0).toFixed(2)}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <button onClick={() => updateCart(p._id, Math.max(0, inCart - 1))} className="w-6 h-6 rounded bg-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-300">-</button>
                          <Input
                            type="number" min="0" value={inCart || ''}
                            onChange={e => updateCart(p._id, parseInt(e.target.value) || 0)}
                            className="h-6 w-12 text-center text-xs p-0"
                          />
                          <button onClick={() => updateCart(p._id, inCart + 1)} className="w-6 h-6 rounded bg-[#1f2548] text-white text-xs font-bold hover:bg-[#2a3060]">+</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Cart summary */}
              {cartItems.length > 0 && (
                <div className="bg-slate-50 rounded-lg border p-3 space-y-2">
                  <h4 className="text-sm font-semibold text-[#1f2548]">{cartItems.length} producten geselecteerd</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {cartItems.map(ci => (
                      <div key={ci._id} className="flex justify-between text-xs">
                        <span className="truncate flex-1">{ci.aantal}x {ci.naam}</span>
                        <span className="font-medium ml-2">&euro;{ci.subtotaal.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-2 space-y-0.5 text-sm">
                    <div className="flex justify-between text-slate-500"><span>Subtotaal:</span><span>&euro;{totals.sub.toFixed(2)}</span></div>
                    <div className="flex justify-between text-slate-500"><span>BTW (21%):</span><span>&euro;{totals.btw.toFixed(2)}</span></div>
                    <div className="flex justify-between font-bold text-[#1f2548]"><span>Totaal:</span><span>&euro;{totals.tot.toFixed(2)}</span></div>
                  </div>
                </div>
              )}

              {/* Notities */}
              <div>
                <Label>Notities (optioneel)</Label>
                <Textarea value={notities} onChange={e => setNotities(e.target.value)} placeholder="Extra opmerkingen..." rows={2} />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSubmit} disabled={cartItems.length === 0} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="catalog-submit-btn">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Bestelling plaatsen ({cartItems.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VoorraadTab;

// Bestelling Modal Component
const BestellingModal = ({ open, onClose, lowStockItems, leveranciers, onSubmit }) => {
  const [selectedItems, setSelectedItems] = useState({});
  const [leverancierNaam, setLeverancierNaam] = useState('');
  const [notities, setNotities] = useState('');

  // Initialize selected items when modal opens
  const initializeItems = React.useCallback(() => {
    const initial = {};
    lowStockItems.forEach(item => {
      const tekort = item.standaardVoorraad - item.huidigeVoorraad;
      initial[item._id] = {
        selected: true,
        aantal: tekort > 0 ? tekort : 1,
        naam: item.naam,
        eenheid: item.eenheid,
        categorie: item.categorie
      };
    });
    return initial;
  }, [lowStockItems]);

  // Reset when modal opens
  React.useEffect(() => {
    if (open) {
      setSelectedItems(initializeItems());
      setLeverancierNaam('');
      setNotities('');
    }
  }, [open, initializeItems]);

  const toggleItem = (itemId) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        selected: !prev[itemId]?.selected
      }
    }));
  };

  const updateAantal = (itemId, aantal) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        aantal: parseInt(aantal) || 0
      }
    }));
  };

  const handleSubmit = () => {
    const items = Object.entries(selectedItems)
      .filter(([_, data]) => data.selected && data.aantal > 0)
      .map(([itemId, data]) => ({
        productId: itemId,
        naam: data.naam,
        aantal: data.aantal,
        eenheid: data.eenheid,
        categorie: data.categorie
      }));

    if (items.length === 0) {
      toast.error('Selecteer minimaal 1 product');
      return;
    }

    if (!leverancierNaam) {
      toast.error('Kies een leverancier — zonder leverancier kan de bestelling niet '
        + 'verstuurd worden');
      return;
    }
    onSubmit({ leverancierNaam, items, notities });

    // Reset
    setSelectedItems({});
    setLeverancierNaam('');
    setNotities('');
  };

  const selectedCount = Object.values(selectedItems).filter(i => i.selected).length;
  const totalItems = Object.values(selectedItems)
    .filter(i => i.selected)
    .reduce((sum, i) => sum + (i.aantal || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Bestelling maken
          </DialogTitle>
          <DialogDescription>
            Selecteer producten en aantallen voor je bestelling
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Leverancier Selection */}
          <div>
            <Label>Leverancier</Label>
            <Select value={leverancierNaam} onValueChange={setLeverancierNaam}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer leverancier (optioneel)" />
              </SelectTrigger>
              <SelectContent>
                {leveranciers.map(lev => (
                  <SelectItem key={lev._id} value={lev.bedrijfsnaam}>
                    {lev.bedrijfsnaam}
                  </SelectItem>
                ))}
                <SelectItem value="Anders">Anders / Nog bepalen</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Products List */}
          <div>
            <Label className="mb-2 block">Producten met lage voorraad</Label>
            <div className="border rounded-lg divide-y">
              {lowStockItems.map(item => {
                const itemData = selectedItems[item._id] || { selected: false, aantal: 0 };
                const tekort = item.standaardVoorraad - item.huidigeVoorraad;
                
                return (
                  <div 
                    key={item._id} 
                    className={`p-3 flex items-center gap-4 ${itemData.selected ? 'bg-blue-50' : ''}`}
                  >
                    <Checkbox
                      checked={itemData.selected}
                      onCheckedChange={() => toggleItem(item._id)}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{item.naam}</div>
                      <div className="text-sm text-slate-500">
                        Voorraad: {item.huidigeVoorraad} / {item.standaardVoorraad} {item.eenheid}
                        <span className="text-red-500 ml-2">(tekort: {tekort})</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        value={itemData.aantal || ''}
                        onChange={(e) => updateAantal(item._id, e.target.value)}
                        className="w-20 text-center"
                        disabled={!itemData.selected}
                      />
                      <span className="text-slate-500 text-sm">{item.eenheid}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notities */}
          <div>
            <Label>Notities (optioneel)</Label>
            <Textarea
              value={notities}
              onChange={(e) => setNotities(e.target.value)}
              placeholder="Extra opmerkingen voor de bestelling..."
              rows={3}
            />
          </div>

          {/* Summary */}
          <Card className="bg-slate-50">
            <CardContent className="pt-4">
              <div className="flex justify-between text-sm">
                <span>Geselecteerde producten:</span>
                <span className="font-semibold">{selectedCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Totaal items:</span>
                <span className="font-semibold">{totalItems}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700">
            <Package className="w-4 h-4 mr-2" />
            Bestelling aanmaken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Bestelling Item Component (for history)
const BestellingItem = ({ bestelling, onStatusChange, onDelete }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'concept':
        return { icon: Clock, color: 'bg-slate-100 text-slate-700', label: 'Concept' };
      case 'verzonden':
        return { icon: Send, color: 'bg-blue-100 text-blue-700', label: 'Verzonden' };
      case 'ontvangen':
        return { icon: CheckCircle2, color: 'bg-green-100 text-green-700', label: 'Ontvangen' };
      default:
        return { icon: Clock, color: 'bg-slate-100 text-slate-700', label: status };
    }
  };

  const statusConfig = getStatusConfig(bestelling.status);
  const StatusIcon = statusConfig.icon;
  const aangemaakt = new Date(bestelling.aangemaakt).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <AccordionItem value={bestelling._id} className="border rounded-lg bg-white">
      <AccordionTrigger className="px-4 hover:no-underline">
        <div className="flex items-center justify-between w-full pr-4">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-slate-400" />
            <div className="text-left">
              <div className="font-medium">
                {bestelling.leverancierNaam || 'Algemene bestelling'}
              </div>
              <div className="text-sm text-slate-500">{aangemaakt}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={statusConfig.color}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </Badge>
            <Badge variant="outline">{bestelling.totaalItems} items</Badge>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        {/* Items List */}
        <div className="bg-slate-50 rounded-lg p-3 mb-4">
          <h4 className="font-medium text-sm text-slate-600 mb-2">Producten</h4>
          <div className="space-y-1">
            {bestelling.items.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span>{item.naam}</span>
                <span className="text-slate-600">{item.aantal} {item.eenheid}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Notities */}
        {bestelling.notities && (
          <div className="text-sm text-slate-600 mb-4">
            <span className="font-medium">Notities: </span>
            {bestelling.notities}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          {bestelling.status === 'concept' && (
            <Button 
              size="sm" 
              onClick={() => onStatusChange(bestelling._id, 'verzonden')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4 mr-1" />
              Markeer als verzonden
            </Button>
          )}
          {bestelling.status === 'verzonden' && (
            <Button 
              size="sm" 
              onClick={() => onStatusChange(bestelling._id, 'ontvangen')}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Markeer als ontvangen
            </Button>
          )}
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => onDelete(bestelling._id)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Verwijderen
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

// BestellingenView Component - Shows order history with actions
const BestellingenView = ({ bestellingen, formatDate, getStatusBadge, onVerzenden, onOntvangen, onDelete, onDownloadPDF, onResendEmail }) => {
  const [expandedOrder, setExpandedOrder] = useState(null);

  if (bestellingen.length === 0) {
    return (
      <div className="text-center py-12 bg-slate-50 rounded-lg">
        <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 text-lg">Nog geen bestellingen geplaatst</p>
        <p className="text-slate-400 text-sm mt-1">Bestellingen verschijnen hier nadat je ze hebt aangemaakt</p>
      </div>
    );
  }

  // Group by status for summary
  const conceptOrders = bestellingen.filter(b => b.status === 'concept');
  const verzondenOrders = bestellingen.filter(b => b.status === 'verzonden');
  const ontvangenOrders = bestellingen.filter(b => b.status === 'ontvangen');

  return (
    <div className="space-y-6">
      {/* Status summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Clock className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{conceptOrders.length}</div>
                <div className="text-sm text-slate-500">Concept</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Send className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-700">{verzondenOrders.length}</div>
                <div className="text-sm text-blue-600">Verzonden</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-700">{ontvangenOrders.length}</div>
                <div className="text-sm text-emerald-600">Ontvangen</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders list */}
      <div className="space-y-3">
        {bestellingen.map((bestelling) => {
          const isExpanded = expandedOrder === bestelling._id;
          
          return (
            <Card key={bestelling._id} className="overflow-hidden">
              {/* Order header */}
              <div 
                className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedOrder(isExpanded ? null : bestelling._id)}
              >
                <div className="flex items-center gap-4">
                  <button className="text-slate-400">
                    {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>
                  <div>
                    <div className="font-medium text-slate-800">
                      {bestelling.leverancierNaam || 'Onbekende leverancier'}
                    </div>
                    <div className="text-sm text-slate-500">
                      {formatDate(bestelling.aangemaakt)} • {bestelling.items?.length || 0} producten
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(bestelling.status)}
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="border-t bg-slate-50">
                  <div className="p-4 space-y-4">
                    {/* Dates */}
                    <div className="flex gap-6 text-sm">
                      <div>
                        <span className="text-slate-500">Aangemaakt:</span>{' '}
                        <span className="font-medium">{formatDate(bestelling.aangemaakt)}</span>
                      </div>
                      {bestelling.verzonden && (
                        <div>
                          <span className="text-slate-500">Verzonden:</span>{' '}
                          <span className="font-medium">{formatDate(bestelling.verzonden)}</span>
                        </div>
                      )}
                      {bestelling.ontvangen && (
                        <div>
                          <span className="text-slate-500">Ontvangen:</span>{' '}
                          <span className="font-medium">{formatDate(bestelling.ontvangen)}</span>
                        </div>
                      )}
                    </div>

                    {/* Items table */}
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-white">
                          <TableHead>Product</TableHead>
                          <TableHead className="text-center">Aantal</TableHead>
                          <TableHead>Eenheid</TableHead>
                          <TableHead className="text-right">Stukprijs</TableHead>
                          <TableHead className="text-right">Subtotaal</TableHead>
                          {bestelling.ontvangenItems && <TableHead className="text-center">Ontvangen</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(bestelling.items || []).map((item, idx) => {
                          const ontvangenItem = bestelling.ontvangenItems?.find(oi => oi.naam === item.naam);
                          return (
                            <TableRow key={idx} className="bg-white">
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {item.foto_url && <img src={item.foto_url} alt="" className="w-8 h-8 rounded object-cover border" />}
                                  <span className="font-medium">{item.naam}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">{item.aantal}</TableCell>
                              <TableCell className="text-slate-500">{item.eenheid}</TableCell>
                              <TableCell className="text-right text-slate-600">
                                {item.prijs_excl_btw != null ? `€${Number(item.prijs_excl_btw).toFixed(2)}` : '-'}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {item.subtotaal != null ? `€${Number(item.subtotaal).toFixed(2)}` : '-'}
                              </TableCell>
                              {bestelling.ontvangenItems && (
                                <TableCell className="text-center">
                                  {ontvangenItem?.ontvangenAantal || ontvangenItem?.aantal || '-'}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>

                    {/* Totals */}
                    {bestelling.items?.some(it => it.subtotaal != null) && (() => {
                      const sub = bestelling.items.reduce((s, it) => s + (it.subtotaal || 0), 0);
                      const btw = bestelling.items.reduce((s, it) => s + (it.btw_bedrag || 0), 0);
                      const tot = bestelling.items.reduce((s, it) => s + (it.totaal_incl_btw || 0), 0);
                      return (
                        <div className="flex justify-end">
                          <div className="bg-white rounded-lg border p-3 space-y-1 text-sm min-w-[200px]">
                            <div className="flex justify-between"><span className="text-slate-500">Subtotaal:</span><span>&euro;{sub.toFixed(2)}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">BTW:</span><span>&euro;{btw.toFixed(2)}</span></div>
                            <div className="flex justify-between font-bold text-[#1f2548] border-t pt-1 mt-1"><span>Totaal:</span><span>&euro;{tot.toFixed(2)}</span></div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Notes */}
                    {bestelling.notities && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="text-sm">
                          <span className="font-medium text-amber-800">Notities:</span>{' '}
                          <span className="text-amber-700">{bestelling.notities}</span>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onDownloadPDF(bestelling._id); }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PDF downloaden
                      </Button>

                      {bestelling.status === 'concept' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={(e) => { e.stopPropagation(); onVerzenden(bestelling._id); }}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Verzenden
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); onDelete(bestelling._id); }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Verwijderen
                          </Button>
                        </>
                      )}

                      {bestelling.status === 'verzonden' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={(e) => { e.stopPropagation(); onOntvangen(bestelling); }}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Ontvangen
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); onResendEmail(bestelling._id); }}
                            data-testid="resend-email-btn"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Email opnieuw versturen
                          </Button>
                        </>
                      )}

                      {bestelling.status === 'ontvangen' && (
                        <div className="flex items-center gap-2 text-emerald-600">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-sm">Afgerond op {formatDate(bestelling.ontvangen)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// OntvangenModal - Modal to confirm received quantities and update stock
const OntvangenModal = ({ open, onClose, bestelling, onConfirm }) => {
  const [ontvangenItems, setOntvangenItems] = useState([]);
  const [bevestigBezig, setBevestigBezig] = useState(false);
  const bezigRef = React.useRef(false);   // F3c: guard vóór de render-return

  // Initialize items when modal opens
  React.useEffect(() => {
    if (open && bestelling?.items) {
      setOntvangenItems(
        bestelling.items.map(item => ({
          ...item,
          ontvangenAantal: item.aantal
        }))
      );
    }
  }, [open, bestelling]);

  const handleUpdateAantal = (index, value) => {
    setOntvangenItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ontvangenAantal: parseInt(value) || 0 };
      return updated;
    });
  };

  const handleConfirm = () => {
    if (!bestelling) return undefined;
    return onConfirm(bestelling._id, ontvangenItems);   // F3c: promise teruggeven
  };

  if (!bestelling) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Bestelling Ontvangen</DialogTitle>
          <DialogDescription>
            Leverancier: <strong>{bestelling.leverancierNaam || 'Onbekend'}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Controleer de ontvangen aantallen. De voorraad wordt automatisch bijgewerkt.
          </p>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="text-center">Besteld</TableHead>
                <TableHead className="text-center">Ontvangen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ontvangenItems.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium">{item.naam}</TableCell>
                  <TableCell className="text-center text-slate-500">
                    {item.aantal} {item.eenheid}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        value={item.ontvangenAantal}
                        onChange={(e) => handleUpdateAantal(idx, e.target.value)}
                        className="w-20 text-center"
                      />
                      <span className="text-sm text-slate-500">{item.eenheid}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Summary of differences */}
          {ontvangenItems.some(item => item.ontvangenAantal !== item.aantal) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Let op: Sommige aantallen wijken af van de bestelling.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={bevestigBezig}>Annuleren</Button>
          <Button onClick={async () => {
            if (bezigRef.current) return;       // E2/F3c: geen dubbele boeking bij dubbeltap
            bezigRef.current = true;
            setBevestigBezig(true);
            try { await handleConfirm(); }
            finally { bezigRef.current = false; setBevestigBezig(false); }
          }} disabled={bevestigBezig} className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="ontvangen-bevestig-btn">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {bevestigBezig ? 'Bezig...' : 'Bevestigen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
